/**
 * The engine's inbox — what this person was actually sent.
 *
 * Served by `GET /notification-inbox`, the recipient's own view: it takes no user
 * id, and every item carries THIS viewer's `isRead`, `state` and actions, worked
 * out on the server. It used to read the operator run log and decide read state
 * in the browser by matching user ids against each fire's recipient list — and
 * when that match missed, every row rendered as read and opening one never marked
 * anything, so read and unread looked identical.
 *
 * Behaves the way the old inbox did:
 *   - clicking a notification opens it and marks it read, immediately;
 *   - unread rows carry a dot and bold title, and each row can be flipped back
 *     to unread ("opened it, haven't dealt with it");
 *   - All / Unread / Read / Actions filters with counts, and mark-all-read.
 *     Read and Unread are exact complements of one shared rule (`read-state`),
 *     so the two counts always add up to All and nothing can fall between them.
 *
 * Opening one reads like a message rather than like a record. Closed, a row is a
 * scanning line: subject, one line of preview, when it arrived. Open, it becomes
 * a letter — subject, a single "from … · when" line, a rule, then the body at a
 * reading measure with its own typography (`.notif-body` in index.css), and the
 * response buttons in a footer below a second rule. Bodies arrive as template
 * HTML, so the preview is stripped to text and the opened body is sanitised and
 * rendered, instead of being printed with its tags showing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { Button } from 'sharedUi/Button';
import { Icon } from '../../components/common/icon';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { LoadingSpinner } from 'sharedUi/LoadingSpinner';
import MemoDialog from '../../components/notifications/memo-dialog';
import SideDrawer from '../../components/common/side-drawer';
import { notificationsRuntimeService } from '../../services/notifications.service';
import type { Notification, NotificationAction } from '../../types';
import { extractErrorMessage } from '../../utils/error-handler';
import { isNotificationUnread } from '../../components/notifications/read-state';
import { needsDialog, performAction } from '../../components/notifications/perform-action';
import { useNavigate } from 'react-router-dom';
import { picturesOf, stripHtml } from '../../components/notifications/notification-format';
import { formatUtcInIst, getRelativeTimeFromUtc } from '../../utils/timezone.util';

type Filter = 'all' | 'unread' | 'read' | 'actions';

/** How many the inbox pulls. Filters and counts work over this whole set. */
const PAGE_SIZE = 100;

/** Absolute time — used where the exact moment matters (an opened message). */
const fmt = (iso?: string | null) => (iso ? formatUtcInIst(iso) : '');

/** Does this body carry markup, or is it already plain text? */
const isHtml = (body?: string | null) => /<[a-z][\s\S]*>/i.test(body || '');

// The same rule the bell uses, so the two can never disagree about one item.
const isUnread = (n: Notification) => isNotificationUnread(n);
const needsAction = (n: Notification) => (n.availableActions?.length ?? 0) > 0;

/** Hide a picture that will not load, rather than leave a broken frame in the feed. */
const hideBroken = (e: { currentTarget: HTMLImageElement }) => {
  e.currentTarget.style.display = 'none';
};

const EngineInbox = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();

  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // An action waiting on its memo / confirmation.
  const [pending, setPending] = useState<{ item: Notification; action: NotificationAction } | null>(null);

  const fail = (message: string) =>
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 8000 });

  const load = useCallback(async () => {
    try {
      const page = await notificationsRuntimeService.getMyNotifications({
        contactId: '', page: 1, pageSize: PAGE_SIZE,
      });
      setItems(page.data || []);
    } catch (e) {
      fail(extractErrorMessage(e, 'Could not load your notifications'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A push arriving means something new is waiting — show it without a refresh.
  useEffect(() => {
    const onPush = () => { load(); };
    window.addEventListener('fcm:message', onPush);
    return () => window.removeEventListener('fcm:message', onPush);
  }, [load]);

  /** Set one item's read flag locally, so the row changes before the network answers. */
  const setRead = (id: string, read: boolean) =>
    setItems((prev) => prev.map((n) => (n._id === id
      ? { ...n, isRead: read, readAt: read ? (n.readAt || new Date().toISOString()) : null }
      : n)));

  const markRead = async (n: Notification) => {
    if (!isUnread(n)) return;
    setRead(n._id, true);
    try {
      await notificationsRuntimeService.markAsRead(n._id);
    } catch (e) {
      setRead(n._id, false);
      fail(extractErrorMessage(e, 'Could not mark it read'));
    }
  };

  const markUnread = async (n: Notification) => {
    // An answered item cannot be made to look pending again — the server refuses
    // it, so the button is not offered for one.
    if (isUnread(n) || n.state === 'ACTIONED') return;
    setRead(n._id, false);
    try {
      await notificationsRuntimeService.markAsUnread(n._id);
    } catch (e) {
      setRead(n._id, true);
      fail(extractErrorMessage(e, 'Could not mark it unread'));
    }
  };

  /** Clicking a notification opens it — and opening it is reading it. */
  const toggleOpen = (n: Notification) => {
    if (openId === n._id) { setOpenId(null); return; }
    setOpenId(n._id);
    void markRead(n);
  };

  const markAllRead = async () => {
    const unreadIds = items.filter(isUnread).map((n) => n._id);
    if (!unreadIds.length) return;
    setItems((prev) => prev.map((n) => ({
      ...n, isRead: true, readAt: n.readAt || new Date().toISOString(),
    })));
    try {
      await notificationsRuntimeService.markAllAsRead();
      toast.current?.show({
        severity: 'success', summary: 'Done',
        detail: `${unreadIds.length} marked read`, life: 2500,
      });
    } catch (e) {
      fail(extractErrorMessage(e, 'Could not mark everything read'));
      load();
    }
  };

  const perform = async (item: Notification, action: NotificationAction, memo?: string) => {
    setBusy(true);
    try {
      await notificationsRuntimeService.recordAction(item._id, { actionId: action.actionId, memo });
      toast.current?.show({ severity: 'success', summary: action.label, detail: 'Done', life: 2500 });
      setPending(null);
      await load();
      // Only after the record succeeds: going somewhere on a failed write would
      // leave the reader believing the action was taken.
      try {
        performAction(action, navigate);
      } catch (effectErr) {
        fail(extractErrorMessage(effectErr, `'${action.label}' was recorded but could not be completed.`));
      }
    } catch (e) {
      fail(extractErrorMessage(e, `Could not ${action.label}`));
    } finally {
      setBusy(false);
    }
  };

  /** An action that needs a note or a confirmation goes through the dialog first. */
  const onAction = (item: Notification, action: NotificationAction) => {
    if (needsDialog(action)) {
      setPending({ item, action });
      return;
    }
    void perform(item, action);
  };

  const counts = useMemo(() => ({
    all: items.length,
    unread: items.filter(isUnread).length,
    read: items.filter((n) => !isUnread(n)).length,
    actions: items.filter(needsAction).length,
  }), [items]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((n) => {
      // Opening an item marks it read, which under Unread would drop it out of
      // the list the instant you clicked it — the row you are reading vanishing
      // mid-sentence. The open one stays put until you close it or move filter.
      const pinned = n._id === openId;
      if (filter === 'unread' && !isUnread(n) && !pinned) return false;
      if (filter === 'read' && isUnread(n) && !pinned) return false;
      if (filter === 'actions' && !needsAction(n)) return false;
      // Search the readable text, not the markup — otherwise a search for
      // "strong" matches every templated body that happens to bold a word.
      if (term && !`${n.title} ${stripHtml(n.body)}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [items, filter, query, openId]);

  /* The message shown in the drawer. Read from `items`, not `visible`: changing
     the filter while one is open should not leave the drawer holding a record
     that is no longer in the list — it closes, which is what `openedItem` going
     undefined does. */
  const openedItem = useMemo(
    () => (openId ? items.find((n) => n._id === openId) : undefined),
    [items, openId],
  );
  const openedPictures = openedItem ? picturesOf(openedItem) : [];
  const openedActions = openedItem?.availableActions || [];
  const openedSender = openedItem
    ? (openedItem.triggeredBy
        ? String(openedItem.triggeredBy)
        : openedItem.triggerType ? `${String(openedItem.triggerType).toLowerCase()} trigger` : 'System')
    : '';

  if (loading) return <div className="flex justify-center p-8"><LoadingSpinner /></div>;

  const filterButton = (key: Filter, label: string) => (
    <Button
      key={key}
      label={`${label} (${counts[key]})`}
      size="small"
      severity={filter === key ? undefined : 'secondary'}
      outlined={filter !== key}
      onClick={() => { setFilter(key); setOpenId(null); }}
      data-testid={`EngineInbox.Filter.${key}`}
    />
  );

  /** The read/unread flip. Stops propagation so it never opens or closes the row. */
  const readToggle = (n: Notification, unread: boolean) => (
    <Button
      // Registry names, not `pi pi-*`: PrimeIcons ships no `pi-envelope-open`,
      // so the unread row — the one row with something to do — drew an empty
      // circle. An open envelope for "mark it read", a closed one for the
      // reverse. The unread button keeps the default accent and the read one
      // stays muted, so the actionable state is the one that catches the eye.
      icon={unread ? 'mail-open' : 'mail'}
      size="small" text rounded severity={unread ? undefined : 'secondary'}
      tooltip={unread ? 'Mark as read' : 'Mark as unread'}
      tooltipOptions={{ position: 'left' }}
      aria-label={unread ? 'Mark as read' : 'Mark as unread'}
      onClick={(e) => { e.stopPropagation(); void (unread ? markRead(n) : markUnread(n)); }}
      data-testid={`EngineInbox.ToggleRead.${n._id}`}
    />
  );

  /* A soft pill rather than a solid tag.
     The amber was right; the weight was not — a filled block shouted over the
     title it was meant to qualify, and three of them down a list read as the
     page's loudest element. Tinted fill, matching ring, a dot to carry the
     colour: the same signal at the weight of a label. */
  const pill = (text: string, tone: 'amber' | 'green' | 'slate', dot = true, title?: string) => {
    const tones = {
      amber: 'bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
      green: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
      slate: 'bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:ring-gray-600',
    };
    const dots = { amber: 'bg-amber-500', green: 'bg-emerald-500', slate: 'bg-gray-400' };
    return (
      <span title={title}
        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[11px] font-semibold ring-1 ${tones[tone]}`}>
        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dots[tone]}`} />}
        {text}
      </span>
    );
  };


  /* How it reached this person. The backend resolves this per recipient, so it
     answers "how did this arrive for me" rather than "what channels was the
     notification configured for" — a fire that fell back from WhatsApp to SMS
     shows SMS here, which is the honest answer.
     Icon plus label on the opened message, icon only in the list: five rows each
     spelling out "WhatsApp" is a column of repeated words, not information. */
  const CHANNEL_META: Record<string, { label: string; icon: string; tone: string }> = {
    email:    { label: 'Email',    icon: 'pi pi-envelope',       tone: 'text-sky-600 dark:text-sky-400' },
    whatsapp: { label: 'WhatsApp', icon: 'pi pi-whatsapp',       tone: 'text-emerald-600 dark:text-emerald-400' },
    sms:      { label: 'SMS',      icon: 'pi pi-comment',        tone: 'text-violet-600 dark:text-violet-400' },
    push:     { label: 'Push',     icon: 'pi pi-mobile',         tone: 'text-amber-600 dark:text-amber-400' },
    inapp:    { label: 'In-app',   icon: 'pi pi-bell',           tone: 'text-blue-600 dark:text-blue-400' },
  };
  const channelsOf = (n: Notification): string[] =>
    (Array.isArray(n.deliveryChannels) ? n.deliveryChannels : []).filter(Boolean) as string[];
  const channelMeta = (c: string) =>
    CHANNEL_META[String(c).toLowerCase()] || { label: c, icon: 'pi pi-send', tone: 'text-gray-500' };

  /** Icon-only, for a list row. Titled so the name is one hover away. */
  const channelIcons = (n: Notification) => {
    const list = channelsOf(n);
    if (!list.length) return null;
    return (
      <span className="inline-flex items-center gap-1">
        {list.map((c) => {
          const m = channelMeta(c);
          return <i key={c} className={`${m.icon} text-[11px] ${m.tone}`} title={`Delivered by ${m.label}`} />;
        })}
      </span>
    );
  };

  /** Icon + name, for the opened message where there is room to say it. */
  const channelChips = (n: Notification) => {
    const list = channelsOf(n);
    if (!list.length) return null;
    return list.map((c) => {
      const m = channelMeta(c);
      return (
        <span key={c}
          className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-[3px] text-[11px] font-semibold text-gray-600 ring-1 ring-gray-200 dark:bg-gray-700/60 dark:text-gray-300 dark:ring-gray-600">
          <i className={`${m.icon} text-[10px] ${m.tone}`} />
          {m.label}
        </span>
      );
    });
  };


  /* What a recipient chose, in words. The server sends the admin-authored label
     for the action; this is the fallback for a notification whose action master
     was deleted, where all that survives is the code. `NOT_ME` as-is reads like
     an error constant on a screen that is otherwise written in sentences. */
  const humanise = (code: string) =>
    code.replace(/[_-]+/g, ' ').trim().toLowerCase().replace(/^./, (c) => c.toUpperCase());

  /* The pill says the humanised CODE, not the admin's description. The codes are
     written as short answers — NOT_ME, IT_WAS_ME, RAISE_OBJECTION — and clean up
     to "Not me", "It was me", "Raise objection". The description is prose for the
     button ("Confirm the notification was seen and understood.") and would be
     worse in a pill than the code it replaced. It is kept as the tooltip, so the
     fuller wording is one hover away rather than lost. */
  const answerOf = (n: Notification) =>
    (n.selectedAction ? humanise(String(n.selectedAction)) : '');

  const statusTags = (n: Notification) => {
    const answer = n.state === 'ACTIONED' ? answerOf(n) : '';
    return (
      <>
        {/* "Replied", not "Responded: NOT_ME" — the person reading this is the
            one who replied, so the pill says what they said, not that an event
            of type response occurred. */}
        {n.state === 'ACTIONED' && pill(
          answer ? `Replied: ${answer}` : 'Replied', 'green', true,
          n.selectedActionLabel ? String(n.selectedActionLabel) : undefined)}
        {needsAction(n) && pill('Action needed', 'amber')}
      </>
    );
  };

  /* The tile standing in for the old 8px dot.
     Priority is on every notification and was never shown, so an urgent message
     looked exactly like a routine one. Colour carries it, the icon says what
     kind of thing it is, and the ring marks unread — three facts in the space
     the dot used. */
  const avatar = (n: Notification, unread: boolean) => {
    const p = (n.priority || '').toUpperCase();
    const urgent = p === 'HIGH' || p === 'URGENT' || p === 'CRITICAL';
    const tone = n.state === 'ACTIONED'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
      : urgent
        ? 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'
        : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-300';
    const icon = n.state === 'ACTIONED' ? 'check-circle' : urgent ? 'alert-triangle' : 'bell';
    return (
      <span className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon name={icon} size={17} />
        {unread && (
          <span
            aria-label="Unread"
            className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white dark:bg-blue-400 dark:ring-gray-800"
          />
        )}
      </span>
    );
  };

  return (
    <div className="flex flex-col gap-3" data-testid="SCR-EngineInbox">
      <Toast ref={toast} position="top-right" />

      <div className="flex items-center gap-2 flex-wrap">
        {filterButton('all', 'All')}
        {filterButton('unread', 'Unread')}
        {filterButton('read', 'Read')}
        {filterButton('actions', 'Actions')}
        <span className="ml-auto p-input-icon-left">
          <InputText value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..." className="p-inputtext-sm w-56"
            data-testid="EngineInbox.Search" />
        </span>
        <Button label="Mark all read" icon="pi pi-check" size="small" severity="secondary" outlined
          disabled={counts.unread === 0} onClick={markAllRead}
          data-testid="EngineInbox.MarkAllRead" />
        <Button icon="pi pi-refresh" size="small" severity="secondary" outlined
          aria-label="Refresh" onClick={() => load()} />
      </div>

      {visible.length === 0 && (
        <p className="text-sm text-gray-400 py-6 text-center">
          {items.length === 0 ? 'Nothing has been sent to you yet.'
            : filter === 'unread' ? "You're all caught up."
              : filter === 'read' ? 'Nothing has been opened yet.'
                : filter === 'actions' ? 'Nothing is waiting on a response.'
                  : 'Nothing matches that.'}
        </p>
      )}

      <div className="flex flex-col gap-2">
        {visible.map((n) => {
          const unread = isUnread(n);
          const isOpen = openId === n._id;
          const pictures = picturesOf(n);
          const preview = stripHtml(n.body);

          return (
            <div
              key={n._id}
              role="button"
              tabIndex={0}
              aria-current={isOpen ? 'true' : undefined}
              // Unread is highlighted in blue, as the old inbox marked it: a blue
              // row with a blue left edge, a blue dot and a bold blue title. Read
              // rows are plain, so the difference is visible at a glance. The row
              // whose message is open in the drawer is RINGED rather than
              // recoloured — it still has to be findable in the list behind the
              // drawer, and it is a selection, no longer an expansion.
              className={`rounded-lg border border-l-4 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 ${
                isOpen
                  ? 'border-blue-400 border-l-blue-600 bg-blue-50 ring-2 ring-blue-500/40 dark:border-blue-700 dark:border-l-blue-400 dark:bg-blue-900/30'
                  : unread
                    // Solid blue-100 across the whole row. blue-50 (#eff6ff) was
                    // indistinguishable from white on this page, which left only
                    // the dot and the title to say a row was unread.
                    ? 'border-blue-300 border-l-blue-600 bg-blue-100 hover:bg-blue-200 dark:border-blue-800 dark:border-l-blue-400 dark:bg-blue-900/40 dark:hover:bg-blue-900/60'
                    : 'border-gray-200 border-l-transparent bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/40'}`}
              onClick={() => toggleOpen(n)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(n); }
              }}
              data-testid={`EngineInbox.Item.${n._id}`}
            >
                <div className="flex items-start gap-3 p-3">
                  {avatar(n, unread)}

                  {pictures.length > 0 && (
                    <span className="relative shrink-0">
                      <img
                        src={pictures[0]}
                        alt=""
                        loading="lazy"
                        onError={hideBroken}
                        className="h-9 w-9 rounded-lg object-cover ring-1 ring-gray-200 dark:ring-gray-700"
                      />
                      {pictures.length > 1 && (
                        <span className="absolute -bottom-1 -right-1 rounded bg-gray-900/80 px-1 font-mono text-[9px] font-bold text-white">
                          +{pictures.length - 1}
                        </span>
                      )}
                    </span>
                  )}

                  <div className="min-w-0 flex-1">
                    {/* Title and time on one line, the title free to truncate:
                        the time is two words and must not be pushed off by a
                        long subject, which is what wrapped these rows before. */}
                    <div className="flex items-baseline gap-3">
                      <span className={`min-w-0 flex-1 truncate text-sm ${unread
                        ? 'font-bold text-ink'
                        : 'font-medium text-gray-700 dark:text-gray-300'}`}>
                        {n.title || 'Notification'}
                      </span>
                      <span className="shrink-0 whitespace-nowrap text-xs text-gray-400">
                        {getRelativeTimeFromUtc(n.createdDateTime)}
                      </span>
                    </div>

                    {preview && (
                      <p className="mt-1 truncate text-xs leading-relaxed text-ink-muted">
                        {preview}
                      </p>
                    )}

                    {/* The qualifiers, on their own line under the message.
                        Inline beside the title they competed with it for the
                        first thing your eye lands on; here they read as notes
                        about the row, and the row keeps one clear headline. */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {statusTags(n)}
                      {/* Category, not notificationCode: the code is a machine
                          key that means nothing to the person reading their own
                          inbox, and a richer row is not a busier one. */}
                      {n.category && pill(n.category, 'slate', false)}
                      {/* How it arrived, as icons. Separated by a hairline so it
                          reads as a different KIND of fact from the pills — those
                          say what the message is, this says how it got here. */}
                      {channelsOf(n).length > 0 && (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="h-3 w-px bg-gray-300 dark:bg-gray-600" />
                          {channelIcons(n)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Pinned to the row's top-right, clear of the wrapping meta
                      line below, so it never shifts as a row gains a pill. */}
                  {n.state !== 'ACTIONED' && (
                    <span className="shrink-0">{readToggle(n, unread)}</span>
                  )}
                </div>
            </div>
          );
        })}
      </div>


      {/* ── The opened message, over the right half ───────────────────────────
          Reading a notification is a different job from scanning the list, and
          it used to happen inside a row: opening one pushed every row below it
          down, so the list you were working through rearranged itself under the
          cursor. The drawer leaves the list exactly where it was. */}
      <SideDrawer
        open={!!openedItem}
        onClose={() => setOpenId(null)}
        ariaLabel="Notification"
        testId="EngineInbox.Drawer"
        /* Priority and trigger sit on a bar across the foot of the panel, each
           behind its own label. In the corner they were two bare words with
           nothing saying which was which — "NORMAL / EVENT" reads as a status
           until you already know the screen. Down here they are labelled, out of
           the message's way, and in the same place on every notification. */
        footer={openedItem && (
          <footer className="shrink-0 border-t border-gray-200 bg-gray-50 px-5 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
            <dl className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[11px]">
              {openedItem.priority && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">Priority:</dt>
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">
                    {String(openedItem.priority)}
                  </dd>
                </div>
              )}
              {openedItem.triggerType && (
                <div className="flex items-center gap-1.5">
                  <dt className="text-gray-400">Trigger:</dt>
                  {/* Verbatim — `manual`, `error`, `event`. */}
                  <dd className="font-semibold text-gray-600 dark:text-gray-300">
                    {String(openedItem.triggerType)}
                  </dd>
                </div>
              )}
            </dl>
          </footer>
        )}
        header={openedItem && (
          <header className="shrink-0 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="text-[15px] font-semibold leading-snug text-ink">
                  {openedItem.title || 'Notification'}
                </h3>
                <p className="mt-1 text-xs text-ink-muted">
                  From <span className="font-medium text-gray-600 dark:text-gray-300">{openedSender}</span>
                  {' · '}
                  {fmt(openedItem.createdDateTime)}
                </p>
                {/* What the message IS — its state, subject and how it reached
                    you. Priority and trigger are not here: they describe the
                    fire rather than the message, and they sit in the right-hand
                    column below the controls. */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {statusTags(openedItem)}
                  {openedItem.category && pill(openedItem.category, 'slate', false)}
                  {channelChips(openedItem)}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                {openedItem.state !== 'ACTIONED' && readToggle(openedItem, isUnread(openedItem))}
                <Button aria-label="Close" icon="pi pi-times" text rounded size="small"
                  onClick={() => setOpenId(null)} data-testid="EngineInbox.Drawer.Close" />
              </div>
            </div>
          </header>
        )}
      >
        {openedItem && (
          <article className="px-5 py-4">
                    {openedItem.body ? (
                      isHtml(openedItem.body) ? (
                        <div
                          className="notif-body max-w-[70ch]"
                          /* Server-supplied template HTML, sanitised against stored XSS. */
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(openedItem.body) }}
                        />
                      ) : (
                        <div className="notif-body notif-body--plain max-w-[70ch]">{openedItem.body}</div>
                      )
                    ) : (
                      <p className="text-sm italic text-gray-400">No message body.</p>
                    )}

                    {/* Full size here, a thumbnail on the closed row: an opened message is
                        where a picture is looked at rather than scanned past. Several sit
                        side by side and wrap, each capped so one tall image cannot push the
                        buttons below it off the screen. */}
                    {openedPictures.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {openedPictures.map((src) => (
                          <img
                            key={src}
                            src={src}
                            alt=""
                            loading="lazy"
                            onError={hideBroken}
                            className="max-h-72 w-auto max-w-full rounded-lg ring-1 ring-gray-200 dark:ring-gray-700"
                          />
                        ))}
                      </div>
                    )}

                    {openedItem.memo && (
                      <div className="mt-4 rounded-md border-l-2 border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-900/40">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">Your note</p>
                        <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{openedItem.memo}</p>
                      </div>
                    )}

                    {openedActions.length > 0 && (
                      <>
                        <div className="mt-4 h-px bg-gray-200 dark:bg-gray-700" />
                        <div className="mt-3 flex flex-wrap gap-2">
                          {openedActions.map((a) => (
                            <Button key={a.actionId} label={a.label} size="small"
                              icon={a.icon || undefined}
                              severity={a.style === 'danger' ? 'danger' : a.style === 'primary' ? undefined : 'secondary'}
                              outlined={a.style !== 'primary' && a.style !== 'danger'}
                              disabled={busy} onClick={() => onAction(openedItem, a)}
                              data-testid={`EngineInbox.Action.${a.actionId}`} />
                          ))}
                        </div>
                      </>
                    )}

                    {openedItem.readAt && (
                      <p className="mt-3 text-[11px] text-gray-400">Read {fmt(openedItem.readAt as string)}</p>
                    )}

                            </article>
        )}
      </SideDrawer>

      <MemoDialog
        open={!!pending}
        notification={pending?.item || null}
        action={pending?.action || null}
        loading={busy}
        onConfirm={(memo) => pending && perform(pending.item, pending.action, memo || undefined)}
        onCancel={() => setPending(null)}
      />
    </div>
  );
};

export default EngineInbox;
